import { type ComponentType, type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, CheckCircle, FileCheck02, FileSearch02, Globe02, LifeBuoy01, Route } from "@untitledui/icons";
import { AnimatePresence, motion, useInView, useReducedMotion } from "motion/react";

import { useMockRefreshTick } from "./use-mock-refresh-tick";

type AgentKey = "care" | "track" | "quote" | "read" | "plan";

type ReadRow = { tone: string; title: string; meta: string };
type PlanRow = { rank: string; text: string };

function asStringArray(value: unknown): string[] {
    return Array.isArray(value) && value.every((x) => typeof x === "string") ? (value as string[]) : [];
}

function asReadRows(value: unknown): ReadRow[] {
    if (!Array.isArray(value)) return [];
    return value.filter((row): row is ReadRow => {
        return (
            typeof row === "object" &&
            row !== null &&
            "tone" in row &&
            "title" in row &&
            "meta" in row &&
            typeof (row as ReadRow).tone === "string" &&
            typeof (row as ReadRow).title === "string" &&
            typeof (row as ReadRow).meta === "string"
        );
    });
}

function asPlanRows(value: unknown): PlanRow[] {
    if (!Array.isArray(value)) return [];
    return value.filter((row): row is PlanRow => {
        return (
            typeof row === "object" &&
            row !== null &&
            "rank" in row &&
            "text" in row &&
            typeof (row as PlanRow).rank === "string" &&
            typeof (row as PlanRow).text === "string"
        );
    });
}

function MockShell({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className="flex min-h-[14rem] flex-col rounded-2xl border border-secondary_alt bg-secondary_hover sm:min-h-[15.5rem]">
            <div className="flex items-center gap-2 border-b border-secondary_alt px-4 py-2.5">
                <span className="size-2 rounded-full bg-current text-primary" />
                <span className="size-2 rounded-full bg-current text-primary/85" />
                <span className="size-2 rounded-full bg-current text-primary/70" />
                <span className="ml-2 truncate text-[10px] font-medium uppercase tracking-wide text-quaternary sm:text-xs">
                    {label}
                </span>
            </div>
            <div className="relative flex-1 overflow-x-hidden overflow-y-visible p-3 sm:p-4">{children}</div>
        </div>
    );
}

const feedEase = [0.22, 1, 0.36, 1] as const;

function feedTransition(reduce: boolean | null) {
    if (reduce) return { duration: 0 };
    return {
        layout: { duration: 0.34, ease: feedEase },
        opacity: { duration: 0.28, ease: feedEase },
        y: { duration: 0.34, ease: feedEase },
    };
}

type StringFeedItem = { id: string; text: string };

/** Un nuovo elemento in cima a ogni tick, l’ultimo esce; id stabili per layout shift sulle righe centrali. */
function useStringFeed(pool: string[], live: number, max: number, keyPrefix: string): StringFeedItem[] {
    const [items, setItems] = useState<StringFeedItem[]>(() => {
        const stamp = new Date().toLocaleTimeString();
        const slice = pool.slice(0, Math.min(max, pool.length));
        return slice.map((text, i, arr) => ({
            id: `${keyPrefix}-seed-${i}`,
            text: i === arr.length - 1 ? `${text} · ${stamp}` : text,
        }));
    });
    const lastApplied = useRef(0);

    useEffect(() => {
        if (live <= lastApplied.current) return;
        lastApplied.current = live;
        const stamp = new Date().toLocaleTimeString();
        const n = Math.max(pool.length, 1);
        const line = pool[live % n] ?? "…";
        setItems((prev) => [{ id: `${keyPrefix}-${live}`, text: `${line} · ${stamp}` }, ...prev.slice(0, max - 1)]);
    }, [live, pool, max, keyPrefix]);

    return items;
}

function CareMock({ messages }: { messages: string[] }) {
    const reduce = useReducedMotion();
    const live = useMockRefreshTick(!reduce);
    const pool = messages.length > 0 ? messages : ["…"];

    const items = useStringFeed(pool, live, 3, "care");

    const tr = feedTransition(reduce);

    return (
        <div className="space-y-2 overflow-visible">
            <AnimatePresence initial={false} mode="popLayout">
                {items.map((item, i) => (
                    <motion.div
                        key={item.id}
                        layout
                        initial={reduce ? false : { y: -22, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={reduce ? undefined : { y: 26, opacity: 0 }}
                        transition={tr}
                        className={`max-w-[92%] rounded-xl border border-secondary_alt px-3 py-2 text-[11px] leading-snug text-primary sm:text-xs ${
                            i % 2 === 0 ? "mr-auto bg-primary/90" : "ml-auto bg-brand-500/15"
                        }`}
                    >
                        {item.text}
                    </motion.div>
                ))}
            </AnimatePresence>
        </div>
    );
}

function TrackMock({ lines }: { lines: string[] }) {
    const reduce = useReducedMotion();
    const live = useMockRefreshTick(!reduce);
    const pool = lines.length > 0 ? lines : ["…"];
    const rows = useStringFeed(pool, live, 3, "track");
    const tr = feedTransition(reduce);

    return (
        <div className="space-y-2 overflow-visible" role="list">
            <AnimatePresence initial={false} mode="popLayout">
                {rows.map((item) => (
                    <motion.div
                        key={item.id}
                        role="listitem"
                        layout
                        initial={reduce ? false : { y: -22, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={reduce ? undefined : { y: 26, opacity: 0 }}
                        transition={tr}
                        className="flex gap-2 rounded-lg border-l-2 border-brand-500 bg-primary/80 px-3 py-2 text-[11px] text-primary sm:text-xs"
                    >
                        <span className="mt-1 size-1.5 shrink-0 rounded-full bg-brand-500" />
                        <span>{item.text}</span>
                    </motion.div>
                ))}
            </AnimatePresence>
        </div>
    );
}

function QuoteMock({ responses }: { responses: string[] }) {
    const reduce = useReducedMotion();
    const live = useMockRefreshTick(!reduce);
    const pool = responses.length > 0 ? responses : ['HTTP 200 { "status": "ok" }'];

    const [items, setItems] = useState<StringFeedItem[]>(() => {
        const stamp = new Date().toLocaleTimeString();
        const slice = pool.slice(0, Math.min(2, pool.length));
        return slice.map((text, i, arr) => ({
            id: `quote-seed-${i}`,
            text: i === arr.length - 1 ? `${text}\n// sync ${stamp}` : text,
        }));
    });
    const lastApplied = useRef(0);

    useEffect(() => {
        if (live <= lastApplied.current) return;
        lastApplied.current = live;
        const stamp = new Date().toLocaleTimeString();
        const n = Math.max(pool.length, 1);
        const line = pool[live % n] ?? "";
        setItems((prev) => [
            { id: `quote-${live}`, text: `${line}\n// sync ${stamp}` },
            ...prev.slice(0, 1),
        ]);
    }, [live, pool]);

    const tr = feedTransition(reduce);

    return (
        <div className="space-y-2 overflow-visible font-mono text-[10px] leading-relaxed text-quaternary sm:text-[11px]">
            <AnimatePresence initial={false} mode="popLayout">
                {items.map((item) => (
                    <motion.pre
                        key={item.id}
                        layout
                        initial={reduce ? false : { y: -22, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={reduce ? undefined : { y: 26, opacity: 0 }}
                        transition={tr}
                        className="whitespace-pre-wrap break-all rounded-lg bg-primary/90 px-2 py-1.5 text-brand-700 dark:text-brand-400"
                    >
                        {item.text}
                    </motion.pre>
                ))}
            </AnimatePresence>
        </div>
    );
}

type ReadFeedRow = ReadRow & { id: string };

function ReadMock({ chips, rows }: { chips: string[]; rows: ReadRow[] }) {
    const reduce = useReducedMotion();
    const live = useMockRefreshTick(!reduce);
    const pool = rows.length > 0 ? rows : [{ tone: "ok", title: "…", meta: "…" }];

    const [items, setItems] = useState<ReadFeedRow[]>(() => {
        const stamp = new Date().toLocaleTimeString();
        const slice = pool.slice(0, Math.min(3, pool.length));
        return slice.map((row, i, arr) => ({
            ...row,
            id: `read-seed-${i}`,
            meta: i === arr.length - 1 ? `${row.meta} · ${stamp}` : row.meta,
        }));
    });
    const lastApplied = useRef(0);

    useEffect(() => {
        if (live <= lastApplied.current) return;
        lastApplied.current = live;
        const stamp = new Date().toLocaleTimeString();
        const n = Math.max(pool.length, 1);
        const row = pool[live % n]!;
        setItems((prev) => [
            { ...row, id: `read-${live}`, meta: `${row.meta} · ${stamp}` },
            ...prev.slice(0, 2),
        ]);
    }, [live, pool]);

    const tr = feedTransition(reduce);

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-wrap gap-1.5">
                {chips.map((c) => (
                    <span
                        key={c}
                        className="rounded-full border border-secondary_alt bg-primary/80 px-2 py-0.5 text-[10px] font-medium text-tertiary sm:text-xs"
                    >
                        {c}
                    </span>
                ))}
            </div>
            <div className="space-y-0 overflow-visible" role="list">
                <AnimatePresence initial={false} mode="popLayout">
                    {items.map((row, index) => (
                        <motion.div
                            key={row.id}
                            role="listitem"
                            layout
                            initial={reduce ? false : { y: -22, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={reduce ? undefined : { y: 26, opacity: 0 }}
                            transition={tr}
                            className="flex gap-2.5 pb-3 last:pb-0"
                        >
                            <div className="flex shrink-0 flex-col items-center pt-0.5">
                                {row.tone === "warn" ? (
                                    <AlertCircle className="size-4 text-warning-500" aria-hidden />
                                ) : (
                                    <CheckCircle className="size-4 text-brand-600" aria-hidden />
                                )}
                                {index < items.length - 1 ? (
                                    <span className="mt-1 w-px min-h-[1rem] flex-1 bg-secondary_alt" aria-hidden />
                                ) : null}
                            </div>
                            <div className="min-w-0 flex-1 rounded-lg border border-secondary_alt bg-primary/85 px-2.5 py-2">
                                <p className="text-[11px] font-medium text-primary sm:text-xs">{row.title}</p>
                                <p className="mt-0.5 text-[10px] text-quaternary sm:text-xs">{row.meta}</p>
                            </div>
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>
        </div>
    );
}

type PlanFeedRow = PlanRow & { id: string };

function PlanMock({ rows }: { rows: PlanRow[] }) {
    const reduce = useReducedMotion();
    const live = useMockRefreshTick(!reduce);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const inView = useInView(containerRef, { amount: 0.35 });

    const pool = rows.length > 0 ? rows : [{ rank: "P0", text: "…" }];

    const [items, setItems] = useState<PlanFeedRow[]>(() =>
        pool.slice(0, Math.min(3, pool.length)).map((row, i) => ({
            ...row,
            id: `plan-seed-${i}`,
        })),
    );
    const lastApplied = useRef(0);

    useEffect(() => {
        if (live <= lastApplied.current) return;
        lastApplied.current = live;
        const n = Math.max(pool.length, 1);
        const row = pool[live % n]!;
        const rankExtra = `${row.rank} · #${String((live % 89) + 11).padStart(2, "0")}`;
        setItems((prev) => [{ ...row, id: `plan-${live}`, rank: rankExtra }, ...prev.slice(0, 2)]);
    }, [live, pool]);

    const tr = feedTransition(reduce);

    return (
        <div ref={containerRef} className="space-y-2 overflow-visible">
            <AnimatePresence initial={false} mode="popLayout">
                {items.map((row, index) => (
                    <motion.div
                        key={row.id}
                        layout
                        initial={reduce ? false : { y: -22, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={reduce ? undefined : { y: 26, opacity: 0 }}
                        transition={tr}
                        className="flex items-start gap-2 rounded-lg border border-secondary_alt bg-primary/85 px-3 py-2"
                    >
                        <motion.span
                            className="mt-0.5 shrink-0 rounded-md bg-brand-500/20 px-1.5 py-0.5 text-[10px] font-bold text-brand-700 tabular-nums dark:text-brand-400"
                            animate={
                                reduce || !inView || index !== 0
                                    ? undefined
                                    : { opacity: [1, 0.72, 1], scale: [1, 1.04, 1] }
                            }
                            transition={{ duration: 2.4, repeat: 2, ease: "easeInOut" }}
                        >
                            {row.rank}
                        </motion.span>
                        <p className="text-[11px] leading-snug text-primary sm:text-xs">{row.text}</p>
                    </motion.div>
                ))}
            </AnimatePresence>
        </div>
    );
}

const agentIcons: Record<AgentKey, ComponentType<{ className?: string }>> = {
    care: LifeBuoy01,
    track: Globe02,
    quote: FileCheck02,
    read: FileSearch02,
    plan: Route,
};

function FeatureBlock({
    agentKey,
    mock,
}: {
    agentKey: AgentKey;
    mock: ReactNode;
}) {
    const { t } = useTranslation("pages:landing");
    const reduce = useReducedMotion();
    const Icon = agentIcons[agentKey];
    const bullets = asStringArray(t(`agents.${agentKey}.features`, { returnObjects: true })).slice(0, 2);
    const mockLabel = t(`features.showcase.${agentKey}.mockLabel`);

    return (
        <motion.article
            initial={{ opacity: 0, y: reduce ? 0 : 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.48, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col gap-5"
        >
            <MockShell label={mockLabel}>{mock}</MockShell>
            <div className="flex gap-4">
                <div className="h-fit shrink-0 rounded-xl bg-brand-500/10 p-3 ring-1 ring-inset ring-brand-500/10">
                    <Icon className="size-6 text-brand-600" aria-hidden />
                </div>
                <div className="min-w-0 space-y-2">
                    <h3 className="text-xl font-semibold text-primary">{t(`agents.${agentKey}.title`)}</h3>
                    <p className="text-sm leading-relaxed text-quaternary">{t(`agents.${agentKey}.description`)}</p>
                    <ul className="space-y-1.5 pt-1">
                        {bullets.map((line) => (
                            <li key={line} className="flex items-start gap-2 text-sm text-primary">
                                <CheckCircle className="mt-0.5 size-4 shrink-0 text-brand-600" aria-hidden />
                                <span>{line}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </motion.article>
    );
}

export function LandingFeaturesShowcase() {
    const { t, i18n } = useTranslation("pages:landing");

    const careMessages = useMemo(
        () => asStringArray(t("features.showcase.care.messages", { returnObjects: true })),
        [t, i18n.language],
    );
    const trackLines = useMemo(
        () => asStringArray(t("features.showcase.track.lines", { returnObjects: true })),
        [t, i18n.language],
    );
    const quoteResponses = useMemo(
        () => asStringArray(t("features.showcase.quote.responses", { returnObjects: true })),
        [t, i18n.language],
    );
    const readChips = useMemo(
        () => asStringArray(t("features.showcase.read.chips", { returnObjects: true })),
        [t, i18n.language],
    );
    const readRows = useMemo(
        () => asReadRows(t("features.showcase.read.rows", { returnObjects: true })),
        [t, i18n.language],
    );
    const planRows = useMemo(
        () => asPlanRows(t("features.showcase.plan.rows", { returnObjects: true })),
        [t, i18n.language],
    );

    return (
        <div className="mt-10 sm:mt-12">
            <div className="grid gap-8 lg:grid-cols-2 lg:gap-10">
                <FeatureBlock agentKey="care" mock={<CareMock messages={careMessages} />} />
                <FeatureBlock agentKey="track" mock={<TrackMock lines={trackLines} />} />
            </div>
            <div className="mt-8 grid gap-8 lg:mt-10 lg:grid-cols-2 lg:gap-10">
                <FeatureBlock agentKey="quote" mock={<QuoteMock responses={quoteResponses} />} />
                <FeatureBlock agentKey="read" mock={<ReadMock chips={readChips} rows={readRows} />} />
            </div>
            <div className="mt-8 flex justify-center lg:mt-10">
                <div className="w-full max-w-3xl">
                    <FeatureBlock agentKey="plan" mock={<PlanMock rows={planRows} />} />
                </div>
            </div>
            <p className="mt-10 text-center text-sm text-quaternary">
                <a
                    href="#cta"
                    className="font-medium text-brand-600 underline decoration-brand-500/30 underline-offset-4 transition-colors hover:text-brand-700 hover:decoration-brand-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 rounded"
                >
                    {t("features.showcase.learnMore")}
                </a>
            </p>
        </div>
    );
}
